/**
 * ensureObjectsLoaded — On-demand fetcher for database objects.
 *
 * Checks the store cache first, then fetches all 4 object types in parallel.
 * Deduplicates concurrent requests via an in-flight promise map.
 *
 * `loadObjects` is the same load with its outcome spelled out: which type
 * listings failed and with what message, plus what the listings that DID
 * succeed returned. A surface that only needs some types (the Query Editor's
 * table picker needs tables + views) can then show those instead of waiting
 * for a complete list that a failing function listing will never produce.
 */

import type { DatabaseEngineId } from '@/core/engines';
import type { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import { fetchObjectsForFolder } from './objectFetchService';
import { useDatabaseStore } from '../store/databaseStore';

const BASE_OBJECT_TYPES: DatabaseObjectType[] = ['table', 'view', 'stored-procedure', 'function'];

/**
 * The on-demand set must equal the PRELOAD's set for the engine, or this
 * function silently narrows what the store holds. On Oracle the preload also
 * lists `package`; fetching four types here and publishing the result as
 * complete emptied the Package Explorer and search for the rest of the session
 * (the entry looked authoritative, so the IPC fallback was skipped too).
 */
function objectTypesFor(engine?: DatabaseEngineId | string): DatabaseObjectType[] {
  return engine === 'oracle' ? [...BASE_OBJECT_TYPES, 'package'] : BASE_OBJECT_TYPES;
}

export type ObjectsLoadFailure = {
  readonly type: DatabaseObjectType;
  readonly error: string;
};

export type ObjectsLoadResult = {
  /** The database's complete object list — set only when every type listing succeeded (it is cached). */
  readonly objects?: DatabaseObject[];
  /** What the listings that succeeded returned, when some failed (not cached). */
  readonly partialObjects?: DatabaseObject[];
  /** The type listings that failed, with the engine's message. Empty on success. */
  readonly failures: readonly ObjectsLoadFailure[];
};

/** In-flight requests keyed by "connectionId:database" */
const inflightRequests = new Map<string, Promise<ObjectsLoadResult>>();

/**
 * Ensure database objects are loaded in the store for the given connection+database.
 * Returns the objects (or undefined on error). Safe to call repeatedly — deduplicates.
 */
export async function ensureObjectsLoaded(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
): Promise<DatabaseObject[] | undefined> {
  return (await loadObjects(connectionId, database, engine)).objects;
}

/** `ensureObjectsLoaded` with the failure detail kept (see the file header). */
export async function loadObjects(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
): Promise<ObjectsLoadResult> {
  // Check cache first
  const cached = useDatabaseStore.getState().getPreloadedObjects(connectionId, database);
  if (cached) return { objects: cached, failures: [] };

  const key = `${connectionId}:${database}`;

  // Deduplicate concurrent calls
  const existing = inflightRequests.get(key);
  if (existing) return existing;

  const promise = doFetch(connectionId, database, engine);
  inflightRequests.set(key, promise);

  try {
    return await promise;
  } finally {
    inflightRequests.delete(key);
  }
}

async function doFetch(
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
): Promise<ObjectsLoadResult> {
  const objectTypes = objectTypesFor(engine);
  try {
    const results = await Promise.all(
      objectTypes.map((type) => fetchObjectsForFolder(type, connectionId, database, engine)),
    );

    // F06 — if any type listing failed, the batch is incomplete. Consumers of
    // `preloadedData` (table picker, FK pickers) treat a cached entry as the
    // COMPLETE object list with no way to express partiality, so caching here
    // would make the failure invisible. Skip the cache; the next call retries.
    const failures = objectTypes.flatMap((type, i) => {
      const error = results[i].error;
      return error ? [{ type, error }] : [];
    });
    if (failures.length > 0) {
      console.warn('[ensureObjectsLoaded] Partial listing, not caching:', failures.map((f) => f.error));
      return {
        partialObjects: results.flatMap((r) => (r.error ? [] : r.objects)),
        failures,
      };
    }

    const fetched = results.flatMap((r) => r.objects);

    // TYPE-PRESERVING merge. This fetches four object types; the explorer's
    // preload fetches five. Publishing only what was fetched here deleted the
    // fifth — on Oracle that is `package`, so the Package Explorer and search
    // lost every package whenever this ran over a preloaded database. Keep the
    // rows of any type this call did not ask for.
    const fetchedTypes = new Set(objectTypes);
    const existing =
      useDatabaseStore.getState().preloadedData[connectionId]?.objects[database] ?? [];
    const untouched = existing.filter((obj) => !fetchedTypes.has(obj.type));
    const allObjects = [...fetched, ...untouched];

    // Publish this ONE database, not a whole-connection snapshot:
    // `setPreloadedData` is the preload's completion signal and would flip
    // `isPreloading` to false (and republish `databases`) while a wave is
    // still fanning out.
    useDatabaseStore.getState().mergePreloadedObjects(connectionId, database, allObjects);

    return { objects: allObjects, failures: [] };
  } catch (err) {
    console.warn('[ensureObjectsLoaded] Failed to fetch objects:', err);
    const error = err instanceof Error ? err.message : 'Unknown error fetching objects';
    return { failures: objectTypes.map((type) => ({ type, error })) };
  }
}
